import { useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import { getMessage } from '../../../utils/messageUtils'
import {
  completeProduct,
  createDefaultProduct,
  insertNewProduct,
  updateProduct,
  type shoppingListProduct,
} from '../../../utils/shoppingListUtils'
import type { ExtraButton } from '../../PopupForm/Popup'
import PopupError from '../../PopupForm/PopupError'
import NumericInput from '../../PopupForm/NumericInput'
import Spinner from '../../Spinner/Spinner'

import './ShoppingListPopupContent.scss'
import type { product } from '../../../utils/productUtils'
import Popup from '../../PopupForm/Popup'

interface ShoppingListPopupContentProps {
  product: shoppingListProduct | null
  user: User
  currentShops: string[]
  completeShoppingList: shoppingListProduct[] | undefined
  allProducts: product[] | undefined
  isMobile: boolean
  setPopupOnOk: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
  setPopupExtraButtons: React.Dispatch<
    React.SetStateAction<ExtraButton[] | undefined>
  >
  closePopup: () => void
  reLoadData: () => void
}

function ShoppingListPopupContent({
  product,
  user,
  currentShops,
  completeShoppingList,
  allProducts,
  isMobile,
  setPopupOnOk,
  closePopup,
  reLoadData,
  setPopupExtraButtons,
}: ShoppingListPopupContentProps) {
  const [newProduct, setNewProduct] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(product?.quantity || 1)
  const [shop, setShop] = useState<string>(product?.shop || '')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Popup
  const [createPopupOpen, setCreatePopupOpen] = useState<boolean>(false)

  const checkProductInputs = () => {
    if (!product && !newProduct.trim()) {
      setErrorMessage(getMessage('product_name_empty'))
      return false
    } else if (!shop.trim()) {
      setErrorMessage(getMessage('product_shop_empty'))
      return false
    }
    return true
  }

  async function saveProduct() {
    //Checks
    if (!checkProductInputs()) return

    setIsLoading(true)
    try {
      let result:
        | { success: boolean; error?: string; newPopup?: boolean }
        | undefined
      if (!product) {
        result = await saveNewProduct()
      } else {
        result = await updateExistingProduct()
      }
      if (result === undefined) {
        // No changes
        closePopup()
        return
      }
      if (result.newPopup) {
        return
      }
      if (!result.success) {
        setErrorMessage(getMessage('product_save_error') + (result.error || ''))
      } else {
        reLoadData()
        closePopup()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const saveNewProduct = async () => {
    const productFromShoppingList = completeShoppingList?.find(
      p => p.product_name?.toLowerCase() === newProduct.trim().toLowerCase()
    )

    // The product is already in the shopping list, even with qty = 0
    if (productFromShoppingList) {
      const newProduct: shoppingListProduct = {
        ...productFromShoppingList,
        shop: shop.trim(),
        quantity: quantity!,
      }
      return await updateProduct(user.id!, newProduct)
    }

    const existingProduct = allProducts?.find(
      p => p.name.toLowerCase() === newProduct.trim().toLowerCase()
    )
    // The product exists, but has never been added to the shopping list
    if (existingProduct) {
      const newProduct: shoppingListProduct = {
        product: existingProduct.id!,
        product_name: existingProduct.name,
        pack: existingProduct.pack,
        shop: shop.trim(),
        quantity: quantity!,
      }
      return await insertNewProduct(user.id!, newProduct)
    }

    // The product does not exist
    setCreatePopupOpen(true)
    return { success: true, newPopup: true }
  }

  const productChanged = () => {
    return product!.quantity !== quantity || product!.shop !== shop
  }

  const updateExistingProduct = async () => {
    const newProduct: shoppingListProduct = { ...product! }
    newProduct.shop = shop.trim()
    newProduct.quantity = quantity!

    if (productChanged()) {
      return (await updateProduct(user.id!, newProduct)) as {
        success: boolean
        error?: string
      }
    } else {
      return undefined
    }
  }

  const clickDeleteProduct = async () => {
    const newProduct = { ...product!, quantity: 0 }
    setIsLoading(true)
    try {
      await updateProduct(user.id!, newProduct)
      reLoadData()
      closePopup()
    } finally {
      setIsLoading(false)
    }
  }

  const clickCompleteProduct = async () => {
    // We update the qty so it can be saved to the storage
    const newProduct = { ...product!, quantity: quantity }
    setIsLoading(true)
    try {
      await completeProduct(user.id!, newProduct)
      reLoadData()
      closePopup()
    } finally {
      setIsLoading(false)
    }
  }

  const createDefaultProductInDB = async () => {
    setIsLoading(true)
    try {
      const result = (await createDefaultProduct(
        user.id!,
        newProduct.trim(),
        shop.trim()
      )) as { success: boolean; error?: string; productId?: number }

      if (!result.success || !result.productId) {
        setErrorMessage(getMessage('product_save_error') + (result.error || ''))
        setCreatePopupOpen(false)
      } else {
        const slProduct: shoppingListProduct = {
          product: result.productId!,
          product_name: newProduct.trim(),
          shop: shop.trim(),
          quantity: quantity!,
        }
        const result2 = await insertNewProduct(user.id!, slProduct)
        if (!result2.success) {
          setErrorMessage(
            getMessage('product_save_error') + (result2.error || '')
          )
          setCreatePopupOpen(false)
        } else {
          reLoadData()
          closePopup()
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onProductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    setNewProduct(name)

    const existingProduct = allProducts?.find(
      p => p.name.toLowerCase() === name.trim().toLowerCase()
    )
    if (existingProduct) {
      setShop(existingProduct.shop || '')
      setQuantity(existingProduct.pack || 1)
    }
  }

  const labelClass = `pop-up-form-label ${isMobile ? 'pop-up-form-label-mobile' : ''}`
  const fieldClass = `pop-up-form-field ${isMobile ? 'pop-up-form-field-mobile' : ''}`
  let productInput = null
  if (product) {
    setPopupExtraButtons([
      {
        label: getMessage('delete'),
        className: 'shopping-list-popup-delete-button popup-delete-button',
        onClick: clickDeleteProduct,
      },
      {
        label: getMessage('complete'),
        className: 'shopping-list-popup-complete-button',
        onClick: clickCompleteProduct,
      },
    ] as ExtraButton[])
  } else if (allProducts) {
    productInput = (
      <div className="pop-up-form-row">
        <span className={labelClass}>{getMessage('product')}:</span>
        <input
          className={fieldClass}
          type="text"
          placeholder={getMessage('product')}
          value={newProduct}
          onChange={onProductChange}
          list="all-products"
        />
        <datalist id="all-products">
          {allProducts.map((product, index) => (
            <option key={index} value={product.name} />
          ))}
        </datalist>
      </div>
    )
  }

  setPopupOnOk(() => saveProduct)

  // The increase goes to the next multiple of the pack. For example, if the pack is 6 and the current quantity is 7, it will go to 12.
  const pack = product?.pack || 1

  const handleQuantityChange = (newValue: number) => {
    // Only snap to next multiple when clicking the + button (detected by exact increment of pack value)
    if (newValue === quantity + pack) {
      const nextMultiple = Math.ceil((quantity + 1) / pack) * pack
      setQuantity(nextMultiple)
    } else {
      setQuantity(newValue)
    }
  }

  return (
    <div className="pop-up-form">
      <PopupError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      {productInput}
      <div className="pop-up-form-row">
        <span className={labelClass}>{getMessage('shop')}:</span>
        <input
          className={fieldClass}
          type="text"
          placeholder={getMessage('product_shop')}
          value={shop}
          onChange={e => setShop(e.target.value)}
          list="product-shops"
        />
        <datalist id="product-shops">
          {currentShops.map((shopOption, index) => (
            <option key={index} value={shopOption} />
          ))}
        </datalist>
      </div>
      {/* {productInput} */}
      <NumericInput
        className={labelClass}
        label={getMessage('quantity')}
        value={quantity!}
        increase={pack}
        setValue={handleQuantityChange}
        isMobile={isMobile}
      />
      <Popup
        isOpen={createPopupOpen}
        isMobile={isMobile}
        message={getMessage('shopping_list_new_product')}
        onOk={createDefaultProductInDB}
        onClose={() => setCreatePopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default ShoppingListPopupContent
